// @storybook-skip: app state-dependent wrapper around shared PreferencesMenu component
import { useThemeContext } from "@contexts/ThemeContext";
import { useAppStore } from "@hooks/useAppStore";
import { useLocale } from "@hooks/useLocale";
import { defaultI18nConfig } from "@memorang/constants/config/defaults";
import ThemeDialog from "@memorang/ui/components/AccountSettingsApplet/dialogs/ThemeDialog";
import { PreferencesMenu as PreferencesMenuView } from "@memorang/ui/components/PreferencesMenu";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageDialog from "../LanguageDialog";
import {
	resolveCurrentLanguageLabel,
	shouldShowLanguagePreference,
} from "./helpers";

type DialogType = "language" | "theme";

type Props = {
	iconColor?: string;
};

export const PreferencesMenu = ({ iconColor }: Props) => {
	const { t } = useTranslation();
	const { themePreference, setThemePreference } = useThemeContext();
	const { currentLocale, availableLanguages } = useLocale();
	const [isMenuVisible, setIsMenuVisible] = useState(false);
	const [openDialog, setOpenDialog] = useState<DialogType>();

	const showLanguageSettings = useAppStore(
		(store) => store.app.accountSettings?.showLanguageSettings ?? false,
	);
	const supportedLocales = useAppStore(
		(store) => (store.app.i18n ?? defaultI18nConfig).supportedLocales,
	);
	const showLanguage = shouldShowLanguagePreference(
		showLanguageSettings,
		supportedLocales.length,
		process.env.NODE_ENV === "development",
	);

	const openPreference = (dialog: DialogType) => {
		setIsMenuVisible(false);
		setOpenDialog(dialog);
	};

	const closeDialog = () => setOpenDialog(undefined);

	return (
		<>
			<PreferencesMenuView
				visible={isMenuVisible}
				accessibilityLabel={t("navbar.more")}
				iconColor={iconColor}
				themeTitle={t("account.theme")}
				themeSubtitle={t(`account.themeOptions.${themePreference}`)}
				languageTitle={showLanguage ? t("language.sectionTitle") : undefined}
				languageSubtitle={
					showLanguage
						? resolveCurrentLanguageLabel(currentLocale, availableLanguages)
						: undefined
				}
				onOpen={() => setIsMenuVisible(true)}
				onDismiss={() => setIsMenuVisible(false)}
				onPressTheme={() => openPreference("theme")}
				onPressLanguage={
					showLanguage ? () => openPreference("language") : undefined
				}
			/>
			<ThemeDialog
				visible={openDialog === "theme"}
				preference={themePreference}
				onSelect={setThemePreference}
				onDismiss={closeDialog}
			/>
			<LanguageDialog
				visible={openDialog === "language"}
				handleClose={closeDialog}
			/>
		</>
	);
};
